import { useAppDispatch } from 'app/hooks';
import { useNavigate } from 'react-router-dom';
import { useAddWebsiteMutation } from '../services';
import { useGetCompaniesQuery } from 'features/company/services';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useForm } from 'hooks/useForm';
import { WEBSITE_FIELDS_SCHEMA, WEBSITE_INITIAL_FIELDS } from '../constants/website-fields';
import { FormEvent, useCallback } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { removeEmpty, trimValues } from 'utils/object-helpers';
import { WebsiteDto } from '../types/dtos';

const useController = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [addWebsite, { isLoading: isAddWebsiteLoading }] = useAddWebsiteMutation();
    const { data: companies, isLoading: isGetCompaniesLoading } = useGetCompaniesQuery(FETCH_ALL_PARAMS);
    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({
        defaultValues: WEBSITE_INITIAL_FIELDS,
        validationSchema: WEBSITE_FIELDS_SCHEMA,
    });

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'website.WEBSITE_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = { ...formData };

        return trimValues(removeEmpty(data)) as WebsiteDto;
    }, [formData]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();
                    await addWebsite(payload).unwrap();
                    handleSuccess();
                    navigate('../websites');
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [addWebsite, getPayload, handleErrors, handleSubmit, handleSuccess, navigate],
    );

    const onCancel = useCallback(() => {
        navigate('../websites');
    }, [navigate]);

    return {
        formData,
        companies,
        onSubmit,
        onCancel,
        onChange,
        isLoading: isGetCompaniesLoading,
        isAddWebsiteLoading: isAddWebsiteLoading,
        errors,
    };
};

export default useController;
